// Google Analytics utility functions for tracking events and page views

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

// Track page views
export const trackPageView = (url: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '', {
      page_path: url,
    });
  }
};

// Track custom events
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

// Track call clicks
export const trackCallClick = (phoneNumber: string) => {
  trackEvent('call_click', {
    event_category: 'engagement',
    event_label: phoneNumber,
    value: 1,
  });
};

// Track form submissions
export const trackFormSubmission = (formName: string) => {
  trackEvent('form_submission', {
    event_category: 'conversion',
    event_label: formName,
    value: 1,
  });
};

// Track blog engagement
export const trackBlogEngagement = (
  action: 'view' | 'read' | 'share',
  blogTitle: string
) => {
  trackEvent('blog_engagement', {
    event_category: 'content',
    event_label: blogTitle,
    action: action,
  });
};

// Track pricing plan clicks
export const trackPricingClick = (planName: string) => {
  trackEvent('pricing_click', {
    event_category: 'conversion',
    event_label: planName,
    value: 1,
  });
};

// Track CTA button clicks
export const trackCTAClick = (ctaName: string, ctaLocation: string) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    location: ctaLocation,
  });
};

// Track video plays (if applicable)
export const trackVideoPlay = (videoTitle: string) => {
  trackEvent('video_play', {
    event_category: 'engagement',
    event_label: videoTitle,
  });
};

// Track search queries
export const trackSearch = (searchQuery: string, resultsCount: number) => {
  trackEvent('search', {
    event_category: 'engagement',
    search_term: searchQuery,
    results_count: resultsCount,
  });
};

// Track outbound link clicks
export const trackOutboundLink = (url: string) => {
  trackEvent('outbound_link_click', {
    event_category: 'engagement',
    event_label: url,
  });
};

// Track scroll depth
export const trackScrollDepth = (percentage: number) => {
  trackEvent('scroll_depth', {
    event_category: 'engagement',
    value: percentage,
  });
};

// Track blog analytics events
export const trackBlogAnalytics = (
  action: 'page_view' | 'engagement' | 'conversion',
  blogId: string,
  blogTitle: string,
  additionalParams?: Record<string, any>
) => {
  trackEvent('blog_analytics', {
    event_category: 'blog_performance',
    event_label: blogTitle,
    blog_id: blogId,
    action: action,
    ...additionalParams,
  });
};

// Track keyword ranking changes
export const trackKeywordRanking = (
  keyword: string,
  position: number,
  change: number
) => {
  trackEvent('keyword_ranking', {
    event_category: 'seo_performance',
    keyword: keyword,
    position: position,
    change: change,
  });
};

// Track conversion funnel
export const trackConversionFunnel = (
  step: string,
  blogId: string,
  value?: number
) => {
  trackEvent('conversion_funnel', {
    event_category: 'conversion_tracking',
    funnel_step: step,
    blog_id: blogId,
    value: value,
  });
};

// Track traffic source
export const trackTrafficSource = (
  source: string,
  blogId: string,
  referrer?: string
) => {
  trackEvent('traffic_source', {
    event_category: 'traffic_analysis',
    source: source,
    blog_id: blogId,
    referrer: referrer,
  });
};

// Track contact form submission as conversion
export const trackContactFormConversion = (formData: {
  name: string;
  phone: string;
  contactMethod: string;
  source: string;
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
      event_category: 'conversion',
      event_label: 'contact_form_submission',
      value: 1,
      currency: 'RSD',
      transaction_id: `contact_${Date.now()}`,
      items: [{
        item_id: 'contact_form',
        item_name: 'Contact Form Submission',
        item_category: formData.contactMethod,
        quantity: 1,
      }],
      user_data: {
        contact_method: formData.contactMethod,
        source_page: formData.source,
      },
    });
  }
};

// Track chatbot interaction as conversion
export const trackChatbotConversion = (interactionData: {
  messageCount: number;
  topic: string;
  resolved: boolean;
  duration: number;
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
      event_category: 'conversion',
      event_label: 'chatbot_interaction',
      value: interactionData.resolved ? 1 : 0.5,
      currency: 'RSD',
      transaction_id: `chatbot_${Date.now()}`,
      items: [{
        item_id: 'chatbot_conversation',
        item_name: 'Chatbot Interaction',
        item_category: interactionData.topic,
        quantity: interactionData.messageCount,
      }],
      engagement_data: {
        message_count: interactionData.messageCount,
        topic: interactionData.topic,
        resolved: interactionData.resolved,
        duration_seconds: interactionData.duration,
      },
    });
  }
};

// Track callback request completion as conversion
export const trackCallbackConversion = (callbackData: {
  type: 'phone' | 'whatsapp' | 'viber' | 'sms';
  phoneNumber: string;
  source: string;
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
      event_category: 'conversion',
      event_label: 'callback_completion',
      value: 1,
      currency: 'RSD',
      transaction_id: `callback_${Date.now()}`,
      items: [{
        item_id: `callback_${callbackData.type}`,
        item_name: 'Callback Request',
        item_category: callbackData.type,
        quantity: 1,
      }],
      callback_data: {
        type: callbackData.type,
        source: callbackData.source,
      },
    });
  }
};

// Track chatbot session start
export const trackChatbotSessionStart = () => {
  trackEvent('chatbot_session_start', {
    event_category: 'engagement',
    event_label: 'chatbot_opened',
    timestamp: Date.now(),
  });
};

// Track chatbot session end
export const trackChatbotSessionEnd = (sessionData: {
  messageCount: number;
  duration: number;
}) => {
  trackEvent('chatbot_session_end', {
    event_category: 'engagement',
    event_label: 'chatbot_closed',
    message_count: sessionData.messageCount,
    duration_seconds: sessionData.duration,
  });
};

// Track chatbot message sent
export const trackChatbotMessage = (messageData: {
  role: 'user' | 'assistant';
  messageLength: number;
  isQuickQuestion: boolean;
}) => {
  trackEvent('chatbot_message', {
    event_category: 'engagement',
    event_label: `chatbot_${messageData.role}_message`,
    message_length: messageData.messageLength,
    is_quick_question: messageData.isQuickQuestion,
  });
};

// Track landing page section views
export const trackLandingPageSectionView = (sectionName: string) => {
  trackEvent('landing_page_section_view', {
    event_category: 'engagement',
    event_label: sectionName,
    section_name: sectionName,
    timestamp: new Date().toISOString(),
  });
};

// Track landing page CTA interactions
export const trackLandingPageCTA = (
  ctaName: string,
  ctaLocation: string,
  ctaType: 'button' | 'link' | 'phone' | 'form'
) => {
  trackEvent('landing_page_cta_click', {
    event_category: 'conversion',
    event_label: ctaName,
    cta_name: ctaName,
    cta_location: ctaLocation,
    cta_type: ctaType,
    timestamp: new Date().toISOString(),
  });
};

// Track landing page form interactions
export const trackLandingPageFormInteraction = (
  formName: string,
  action: 'view' | 'start' | 'complete' | 'abandon',
  fieldsFilled?: number
) => {
  trackEvent('landing_page_form_interaction', {
    event_category: 'conversion',
    event_label: formName,
    form_name: formName,
    form_action: action,
    fields_filled: fieldsFilled,
    timestamp: new Date().toISOString(),
  });
};

// Track landing page scroll milestones
export const trackLandingPageScrollMilestone = (
  percentage: number,
  sectionReached?: string
) => {
  trackEvent('landing_page_scroll_milestone', {
    event_category: 'engagement',
    event_label: `${percentage}% scrolled`,
    scroll_percentage: percentage,
    section_reached: sectionReached,
    timestamp: new Date().toISOString(),
  });
};

// Track landing page time on section
export const trackLandingPageTimeOnSection = (
  sectionName: string,
  timeSpent: number
) => {
  trackEvent('landing_page_time_on_section', {
    event_category: 'engagement',
    event_label: sectionName,
    section_name: sectionName,
    time_spent_seconds: timeSpent,
    timestamp: new Date().toISOString(),
  });
};

// Track landing page video interaction
export const trackLandingPageVideoInteraction = (
  videoTitle: string,
  action: 'play' | 'pause' | 'complete',
  currentTime?: number
) => {
  trackEvent('landing_page_video_interaction', {
    event_category: 'engagement',
    event_label: videoTitle,
    video_title: videoTitle,
    video_action: action,
    current_time: currentTime,
    timestamp: new Date().toISOString(),
  });
};